import React, { useState, useEffect, useCallback, useRef } from "react";
import classNames from "classnames";
import { motion } from "framer-motion";
import {
  Zap,
  Calendar,
  Percent,
  DollarSign,
  CheckCircle,
  Loader2,
  TrendingUp,
  Sparkles,
  AlertCircle,
} from "lucide-react";

/**
 * Context API
 */
import useModal from "hooks/use-modal";

/**
 * Components
 */
import BaseButton from "components/base-button/base-button";
import PlanBar from "components/plan-bar/plan-bar";
import CurrencyInput from "../../components/currency-input/currency-input";
import { formatMoney } from "../../utils/formatMoney";
import API from "../../utils/api";

const FormOffer = ({
  account,
  offers,
  mode,
  greaterThan,
  prefillAmount = false,
}) => {
  const { openModal, modalTypes } = useModal();
  const [amount, setAmount] = useState("");
  const [period, setPeriod] = useState(mode || "one-time");
  const [fullPayment, setFullPayment] = useState(false);
  const [plan, setPlan] = useState([]);
  const [error, setError] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState();
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const fetchingRef = useRef(false);

  // Animation variants
  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const updatePlan = (index) => {
    const newPlans = plan.map((item, ind) => {
      if (index === ind) {
        item.active = true;
        setSelectedPlan(item);
      } else {
        item.active = false;
      }
      return item;
    });
    setPlan(newPlans);
  };

  const submit = async () => {
    if (period === "settlement-offer") {
      // Continue to payment modal for offer submission instead of submitting directly
      openModal(modalTypes.ModalMakePayment, {
        account,
        amount,
        mode: "settlement-offer",
        back: modalTypes.ModalOffer,
        backData: { account, offers, mode, greaterThan },
      });
    } else if (period === "one-time") {
      openModal(modalTypes.ModalMakePayment, {
        account,
        amount,
        back: modalTypes.ModalOffer,
        backData: { account, offers, mode, greaterThan },
      });
    } else if (period === "payment-plan") {
      console.log(selectedPlan);
      openModal(modalTypes.ModalMakePayment, {
        account,
        amount,
        selectedPlan,
        mode: "payment-plan",
        back: modalTypes.ModalOffer,
        backData: { account, offers, mode, greaterThan },
      });
    }
  };

  const updateAmount = (e) => {
    const newAmt = e.target.value;
    setAmount(newAmt);
    const cur_bal = Number(account.balance_current);
    let amt = Number(newAmt.replace(/[$,]+/g, ""));
    if (period === "one-time") {
      if (amt > cur_bal || amt === 0 || isNaN(amt)) {
        setError(true);
        return;
      }
      if (error) setError(false);
      if (amt === account.balance_current) {
        setFullPayment(true);
      }
    }
  };

  const checkDisabled = useCallback(() => {
    // Disable if one-time is selected but not eligible
    if (period === "one-time" && paymentConfig?.single_eligible !== true) {
      return true;
    }
    if (period === "one-time" && amount === "") {
      return true;
    }
    if (period === "settlement-offer" && amount === "") {
      return true;
    }
    if (period === "payment-plan" && selectedPlan === undefined) {
      return true;
    }
    if (error) return true;
    return false;
  }, [period, amount, selectedPlan, error, paymentConfig?.single_eligible]);

  const fetchPaymentPlans = async () => {
    // Prevent duplicate fetches using ref that persists across StrictMode re-renders
    console.log(
      "[form-offer] fetchPaymentPlans called, fetchingRef.current:",
      fetchingRef.current,
      "account.id:",
      account.id
    );
    if (fetchingRef.current) {
      console.log("[form-offer] Skipping fetch - already in progress");
      return;
    }

    try {
      console.log("[form-offer] Making API call to /payment_plans/config");
      fetchingRef.current = true;
      setLoadingConfig(true);
      const response = await API.get(
        "/accounts/" + account.id + "/payment_plans/config"
      );
      if (response.data) {
        // Store the payment config for use in rendering
        // Ensure single_eligible is explicitly boolean (false if not exactly true)
        setPaymentConfig({
          ...response.data,
          single_eligible: response.data.single_eligible === true,
        });

        let newPlans = [];
        const cur_bal = Number(account.balance_current);

        // Check if payment plans are eligible
        if (response.data.eligible && response.data.max_months) {
          const maxMonths = response.data.max_months;

          // Create multiple month options for the slider (max 6 options)
          // Prioritize showing 3, 6, 12, 24, 36, 48 when possible
          let monthOptions = [];
          const preferredOptions = [3, 6, 12, 24, 36, 48];

          if (maxMonths < 3) {
            // For very short terms (less than 3 months), show what's available
            for (let i = 2; i <= maxMonths; i++) {
              monthOptions.push(i);
            }
          } else {
            // Filter preferred options to only include those <= maxMonths
            monthOptions = preferredOptions.filter((m) => m <= maxMonths);

            // If maxMonths is not in our preferred options and we have less than 6 options,
            // add it as the last option
            if (!monthOptions.includes(maxMonths) && monthOptions.length < 6) {
              monthOptions.push(maxMonths);
            }

            // If we still have less than 6 options and there's room, fill in some gaps
            if (monthOptions.length < 6) {
              // Add intermediate values if needed
              if (
                maxMonths >= 18 &&
                !monthOptions.includes(18) &&
                monthOptions.length < 6
              ) {
                monthOptions.push(18);
                monthOptions.sort((a, b) => a - b);
              }
              if (
                maxMonths >= 9 &&
                !monthOptions.includes(9) &&
                monthOptions.length < 6
              ) {
                monthOptions.push(9);
                monthOptions.sort((a, b) => a - b);
              }
            }

            // Ensure we have at most 6 options (shouldn't happen with preferred options, but just in case)
            if (monthOptions.length > 6) {
              monthOptions = monthOptions.slice(0, 6);
            }
          }

          // Create plan objects for each month option
          monthOptions.forEach((months, index) => {
            const monthlyPayment = cur_bal / months;

            if (!greaterThan || (greaterThan && monthlyPayment > greaterThan)) {
              // For payment-plan mode, set the last plan (max months) as active
              const isActive =
                mode === "payment-plan"
                  ? index === monthOptions.length - 1
                  : index === 0;

              newPlans.push({
                id: `plan_${months}`,
                text: `${months} months`,
                amount: cur_bal,
                price: monthlyPayment,
                active: isActive,
              });
            }
          });

          // If we have plans, set the selected one based on mode
          if (newPlans.length > 0) {
            if (mode === "payment-plan") {
              // Select the last plan (max months) for payment-plan mode
              setSelectedPlan(newPlans[newPlans.length - 1]);
            } else {
              // Select the first plan for other modes
              setSelectedPlan(newPlans[0]);
            }
          }
        }

        setPlan(newPlans);
      } else {
        setPlan([]);
      }
      setLoadingConfig(false);
    } catch (e) {
      fetchingRef.current = false; // Reset on error to allow retry
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    console.log("[form-offer] useEffect triggered, account?.id:", account?.id);
    if (account && account.id) {
      fetchPaymentPlans();

      // Pre-fill amount only for "Pay in Full" option (when prefillAmount is true)
      if (mode === "one-time" && prefillAmount) {
        const balance = account.balance_current || account.balance;
        if (balance) {
          const formattedAmount = "$" + formatMoney(balance);
          setAmount(formattedAmount);
          setFullPayment(true);
        }
      }
      // For custom payment (prefillAmount is false), leave amount blank
    }

    // Cleanup function to reset ref when component unmounts
    return () => {
      console.log("[form-offer] Component unmounting, resetting fetchingRef");
      fetchingRef.current = false;
    };
  }, [account?.id, mode, prefillAmount]);

  // Auto-select first visible payment option when config loads
  React.useEffect(() => {
    if (!loadingConfig && paymentConfig) {
      // Check if current period is visible
      const isOneTimeVisible = paymentConfig.single_eligible === true && (mode === undefined || mode === "one-time");
      const isPlanVisible = plan.length > 0 && (mode === undefined || mode === "payment-plan");
      const isSettlementVisible = (paymentConfig.offer_eligible && (mode === undefined || mode === "settlement-offer")) ||
        (offers && (offers.offer_mode === 1 || offers.offer_mode === 2) && (mode === undefined || mode === "settlement-offer"));

      // If current period isn't visible, select the first visible one
      if (period === "one-time" && !isOneTimeVisible) {
        if (isPlanVisible) {
          setPeriod("payment-plan");
        } else if (isSettlementVisible) {
          setPeriod("settlement-offer");
        }
      }
    }
  }, [loadingConfig, paymentConfig, plan, offers, mode, period]);

  // Payment option configuration
  const paymentOptions = [
    {
      id: "one-time",
      icon: Zap,
      title: "One Time Payment",
      description: "Make a single payment toward the full amount",
      color: "blue",
      badge: fullPayment ? "Pay in Full" : null,
      visible:
        paymentConfig?.single_eligible === true &&
        (mode === undefined || mode === "one-time"),
    },
    {
      id: "payment-plan",
      icon: Calendar,
      title: "Payment Plan",
      description: "Set up recurring payments on your schedule",
      color: "green",
      badge: null,
      visible:
        plan.length > 0 && (mode === undefined || mode === "payment-plan"),
    },
    {
      id: "settlement-offer",
      icon: Percent,
      title: "Resolution Offer",
      description: "Resolve your account for less than the full amount",
      color: "purple",
      badge: null,
      visible:
        (offers !== undefined &&
          offers !== null &&
          (mode === undefined || mode === "settlement-offer") &&
          (offers.offer_mode === 1 || offers.offer_mode === 2)) ||
        (paymentConfig &&
          paymentConfig.offer_eligible &&
          (mode === undefined || mode === "settlement-offer")),
    },
  ];

  const activeOption = paymentOptions.find((opt) => opt.id === period);
  const visibleOptions = paymentOptions.filter((opt) => opt.visible);
  const hasNoOptions = !loadingConfig && visibleOptions.length === 0;

  return (
    <div className="form">
      {loadingConfig ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-8 sm:py-12"
        >
          <div className="relative">
            <div className="w-16 h-16 border-4 border-gray-700 border-t-blue-500 rounded-full animate-spin" />
            <DollarSign className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-600 w-6 h-6" />
          </div>
          <p className="mt-4 text-gray-400">Loading payment options...</p>
        </motion.div>
      ) : hasNoOptions ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-8 sm:py-12 text-center"
        >
          <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-gray-400 mb-2">No payment options available</p>
          <p className="text-gray-500 text-sm">Please contact us for assistance with your account.</p>
        </motion.div>
      ) : (
        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
        >
          {/* Payment Type Selection */}
          <motion.div
            variants={itemAnimation}
            className="space-y-2 sm:space-y-3 mb-4 sm:mb-6"
          >
            <label className="text-xs sm:text-sm font-semibold text-gray-300 uppercase tracking-wider">
              Payment Type
            </label>

            <div className="space-y-2 sm:space-y-3">
              {paymentOptions
                .filter((opt) => opt.visible)
                .map((option) => (
                  <motion.div
                    key={option.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setPeriod(option.id)}
                    className={classNames(
                      "relative p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all",
                      "hover:shadow-lg",
                      period === option.id
                        ? option.color === "blue"
                          ? "border-blue-500 bg-blue-500/10"
                          : option.color === "green"
                            ? "border-green-500 bg-green-500/10"
                            : "border-purple-500 bg-purple-500/10"
                        : "border-gray-700 bg-gray-800/30 hover:border-gray-600"
                    )}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div
                        className={classNames(
                          "w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                          period === option.id
                            ? option.color === "blue"
                              ? "bg-blue-500/20"
                              : option.color === "green"
                                ? "bg-green-500/20"
                                : "bg-purple-500/20"
                            : "bg-gray-700/50"
                        )}
                      >
                        <option.icon
                          className={classNames(
                            "w-4 h-4 sm:w-5 sm:h-5",
                            period === option.id
                              ? option.color === "blue"
                                ? "text-blue-400"
                                : option.color === "green"
                                  ? "text-green-400"
                                  : "text-purple-400"
                              : "text-gray-400"
                          )}
                        />
                      </div>

                      <div className="flex-1 min-w-0 pr-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-white mb-0.5 sm:mb-1 text-sm sm:text-base">
                              {option.title}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-400">
                              {option.description}
                            </p>
                          </div>
                          {option.badge && (
                            <span
                              className={classNames(
                                "px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-bold rounded-full flex-shrink-0",
                                option.color === "blue"
                                  ? "bg-blue-500/20 text-blue-400"
                                  : option.color === "green"
                                    ? "bg-green-500/20 text-green-400"
                                    : "bg-purple-500/20 text-purple-400"
                              )}
                            >
                              {option.badge}
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        className={classNames(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                          period === option.id
                            ? option.color === "blue"
                              ? "border-blue-500 bg-blue-500"
                              : option.color === "green"
                                ? "border-green-500 bg-green-500"
                                : "border-purple-500 bg-purple-500"
                            : "border-gray-600"
                        )}
                      >
                        {period === option.id && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2 h-2 bg-white rounded-full"
                          />
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
            </div>
          </motion.div>

          {/* Amount Input Section - Only show for one-time (if eligible) or settlement-offer */}
          {((period === "one-time" && paymentConfig?.single_eligible === true) ||
            period === "settlement-offer") && (
            <div className="mb-4 sm:mb-6">
              <label className="text-xs sm:text-sm font-semibold text-gray-300 uppercase tracking-wider mb-2 sm:mb-3 block">
                {period === "settlement-offer"
                  ? "What are you offering to pay?"
                  : "Payment Amount"}
              </label>

              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <DollarSign className="w-5 h-5 text-gray-500" />
                </div>
                <CurrencyInput
                  id="amount"
                  placeholder="0.00"
                  type="text"
                  autoComplete="off"
                  value={amount}
                  onChange={(e) => updateAmount(e)}
                  maskOptions={{ prefix: "" }}
                  className={classNames(
                    "w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-gray-800/50 border-2 rounded-xl",
                    "text-white placeholder-gray-500 text-sm sm:text-base",
                    "focus:outline-none focus:border-blue-500 focus:bg-gray-800/70",
                    "transition-all duration-200",
                    error ? "border-red-500" : "border-gray-700"
                  )}
                />
                {error && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                )}
              </div>

              {error && (
                <p className="mt-2 text-sm text-red-400">
                  Please enter a valid amount
                </p>
              )}

              {/* Resolution Offer Disclaimer */}
              {period === "settlement-offer" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-300 mb-2">
                        Important Notice:
                      </p>
                      <p className="text-xs text-yellow-200 leading-relaxed">
                        You can make an offer to resolve your account for less
                        than the full amount you owe. If accepted, payment for
                        this arrangement will be processed at the date you have
                        selected. If you leave before finalizing your payment,
                        you may be able to present a new offer in the future. We
                        are not obligated to renew our acceptance of your offer
                        in the future and our acceptance of your offer does not
                        affect or take away other rights previously disclosed to
                        you.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Payment Plan Slider */}
          {plan.length > 0 && period === "payment-plan" && (
            <div className="mb-4 sm:mb-6">
              <label className="text-xs sm:text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3 sm:mb-4 block">
                Choose Your Plan Duration
              </label>
              <PlanBar plans={plan} change={updatePlan} />
            </div>
          )}

          {/* Submit Button */}
          <motion.div variants={itemAnimation}>
            <BaseButton
              type="submit"
              onClick={() => submit()}
              disabled={checkDisabled()}
              className={classNames(
                "w-full py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base",
                "transition-all duration-200",
                "flex items-center justify-center gap-2",
                checkDisabled()
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : activeOption?.color === "blue"
                    ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl"
                    : activeOption?.color === "green"
                      ? "bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white shadow-lg hover:shadow-xl"
                      : "bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white shadow-lg hover:shadow-xl"
              )}
            >
              <>
                {period === "settlement-offer" && (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Continue to Offer
                  </>
                )}
                {period === "one-time" && (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Continue to Payment
                  </>
                )}
                {period === "payment-plan" && (
                  <>
                    <TrendingUp className="w-5 h-5" />
                    Set Up Plan
                  </>
                )}
              </>
            </BaseButton>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default FormOffer;
